import { captureException, withScope } from '@sentry/react';

import { sentryState } from '@/store/SearchStore';

type CacheEntry<T> = {
  data: T | undefined;
  expiry: number;
};

const directionsCache = new Map<string, CacheEntry<google.maps.DirectionsResult>>();

const CACHE_EXPIRATION = 5 * 60 * 1000; // 5 min

export const getDirections = async (
  locationStart: google.maps.places.Place,
  locationEnd: google.maps.places.Place,
  travelMode: google.maps.TravelMode,
) => {
  const cacheKey = JSON.stringify({
    start: locationStart?.id,
    end: locationEnd?.id,
    mode: travelMode,
  });

  const cachedEntry = directionsCache.get(cacheKey);
  const now = Date.now();

  if (cachedEntry && cachedEntry.expiry > now) {
    return cachedEntry.data;
  }

  const directionsService = new google.maps.DirectionsService();

  const request = {
    origin: { placeId: locationStart?.id },
    destination: { placeId: locationEnd?.id },
    travelMode,
    provideRouteAlternatives: true,
    unitSystem: google.maps.UnitSystem.IMPERIAL,
  };

  const result = await new Promise<google.maps.DirectionsResult | undefined>((resolve) => {
    directionsService.route(request, (result, status) => {
      if (status === google.maps.DirectionsStatus.OK) {
        resolve(result || undefined);
      } else {
        resolve(undefined);
      }
    });
  });

  if (!result) {
    withScope((scope) => {
      scope.setContext('searchState', sentryState());
      captureException(new Error('Unable to get map routes from Google Maps API'));
    });
  }

  directionsCache.set(cacheKey, {
    data: result,
    expiry: now + CACHE_EXPIRATION,
  });

  return result;
};

const convertPlaceResultToPlace = (placeResult: google.maps.places.PlaceResult): google.maps.places.Place => {
  return {
    id: placeResult.place_id || '',
    name: placeResult.name || '',
    displayName: placeResult.name || '',
    address: placeResult.formatted_address || '',
    components: placeResult.address_components || [],
    location: placeResult.geometry!.location,
    types: placeResult.types || [],
  } as unknown as google.maps.places.Place;
};

export const getPlaceFromCoordinates = async (lat: number, lng: number): Promise<google.maps.places.Place | null> => {
  return new Promise((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, geoStatus) => {
      if (geoStatus !== google.maps.GeocoderStatus.OK || !results || results.length === 0) {
        return reject(new Error('Geocoder failed due to: ' + geoStatus));
      }

      const geocodeResult = results[0];
      const placeId = geocodeResult.place_id;
      if (!placeId) {
        return resolve(null);
      }

      const dummyDiv = document.createElement('div');
      const placesService = new google.maps.places.PlacesService(dummyDiv);

      const detailsRequest: google.maps.places.PlaceDetailsRequest = {
        placeId,
        fields: ['place_id', 'name', 'formatted_address', 'address_components', 'geometry', 'types'],
      };

      placesService.getDetails(detailsRequest, (placeResult, placesStatus) => {
        if (placesStatus !== google.maps.places.PlacesServiceStatus.OK || !placeResult) {
          return reject(new Error('PlacesService getDetails failed due to: ' + placesStatus));
        }
        const place: google.maps.places.Place = convertPlaceResultToPlace(placeResult);
        resolve(place);
      });
    });
  });
};
export const getGoogleMapLink = (route?: google.maps.DirectionsRoute, travelMode?: google.maps.TravelMode) => {
  if (!route || !travelMode) {
    return undefined;
  }
  const travelModeParam = travelMode === google.maps.TravelMode.BICYCLING ? 'bicycling' : 'walking';
  const steps = route.legs[0].steps;
  const waypoints = steps
    .slice(0, -1)
    .map((step) => `${step.end_location.lat()},${step.end_location.lng()}`)
    .join('|');

  return `https://www.google.com/maps/dir/?api=1&origin=${route.legs[0].start_location.lat()},${route.legs[0].start_location.lng()}&destination=${route.legs[0].end_location.lat()},${route.legs[0].end_location.lng()}&travelmode=${travelModeParam}&waypoints=${waypoints}`;
};

export const decodePolyline = (encoded: string): google.maps.LatLngLiteral[] => {
  const points: google.maps.LatLngLiteral[] = [];
  let index = 0;
  const len = encoded.length;
  let lat = 0;
  let lng = 0;

  while (index < len) {
    let shift = 0;
    let result = 0;

    do {
      const b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (result >= 0x20);

    const dlat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;

    do {
      const b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (result >= 0x20);

    const dlng = result & 1 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    points.push({ lat: lat * 1e-5, lng: lng * 1e-5 });
  }

  return points;
};
